import { Injectable } from '@angular/core';
import { HttpRequest, HttpParams, HttpHeaders } from '@angular/common/http';

import { ISearchEventActivitiesPayload, RequestMethod } from './api-internal.interfaces';
import { ApiError, ICommunicationError, IApiServiceConfig, IPagedRequest, IGetTokenResponse, ITokenInfo, ISearchEventsPayload, ISearchContactsPayload, IAdvancedSearchRequest, SortDirection } from './interfaces';
import { CalendarType } from './enums';

@Injectable({
    providedIn: 'root'
})
export class ApiUtilsService {

    config: IApiServiceConfig;

    private paths = {
        builder: {
            getLoginPage: 'w/event/{eventID}/login',
            // getPage: 'w/page/{pageID}',
            getPage: 'w/page_content/{pageID}',
            // getHomePage: 'w/home',
            getHomePage: 'w/page_content/home',
            getResetPasswordPage: 'w/page_content/password',
            getDependencies: 'w/dependency',
            getMenu: 'w/menu',
            getScripts: 'w/scripts'
        },
        identity: {
            sendEmailAddress: 'identity/send_email',
            getToken: 'identity/token',
            getTokenFromLinkToken: 'identity/link_token',
            impersonate: 'identity/impersonate'
        },
        event: {
            getToken: 'w/event/{eventID}/token',
            refreshToken: 'w/refresh_token',
            getPublicToken: 'w/event/{eventID}/token/public',
            getParticipantFields: 'w/participant/field',
            getFields: 'w/event/field',
            updateParticipant: 'w/participant',
            createParticipant: 'w/participant',
            getSettings: 'w/event/{eventID}/settings',
            search: 'w/event/search',
            participantStageAction: 'w/participant/{participantID}/stage_action/{stageID}',
        },
        eventActivities: {
            search: 'e/activity/search',
            like: 'e/activity/{activityID}/like',
            unlike: 'e/activity/{activityID}/unlike'
        },
        eventActivitiesCart: {
            get: 'w/activity/cart',
            register: 'w/activity/cart'
        },
        eventActivityCalendar: {
            get: 'w/activity/{activityId}/calendar/{calendarType}'
        },
        process: {
            getForm: 'w/form',
            completeForm: 'w/form/{formID}/complete',
            cancel: 'w/cancel'
        },
        payment: {
            product: 'p/product',
            pay: 'p/pay',
            thankYou: 'w/payment_thankyou',
            order: 'p/order',
            checkDiscountCode: 'p/check_discount_code',
            paymentIntegrationWebhook: 'payment/integration/{integrationID}/webhook',
            paymentIntegrationRedirect: 'payment/integration/{integrationID}/redirect?txn={transactionID}'
        },
        document: {
            create: 'w/document',
            update: 'w/document/{documentID}',
            getFile: 'document/{documentID}/file?token={token}',
            getDownloadToken: 'w/document/{documentID}/token'
        },
        profile: {
            token: 'c/oauth/token',
            // getEvents: 'c/event',
            getProfilePage: 'c/profile_page',
            changePassword: 'c/password',
            getOrders: 'c/order',
            updateContact: 'c/contact',
            sendConfirmationCode: 'w/send_confirmation_code',
            searchContacts: 'w/contact/search',
            inviteContact: 'c/contact/invite',
            getEmailCampaignSubscriptions: 'c/email_campaign/subscription',
            setEmailCampaignSubscriptions: 'c/email_campaign/subscription',
            sendPasswordResetLink: 'w/send_passwordresetlink',
            sendLoginLink: 'w/send_loginlink',
            sendCreateAccountLink: 'w/send_createaccountlink',
            accessCodeLogIn: 'w/event/{eventID}/accesscode'
        },
    };

    getSearchEventActivitiesPayload(activitySearchText: string): ISearchEventActivitiesPayload {
        const payload: ISearchEventActivitiesPayload = {
            activity_filter: {}
        };
        if (activitySearchText) {
            payload.activity_filter = {
                or: [
                    { name: { like: activitySearchText } }
                ]
            };
        }
        return payload;
    }

    getSearchContactsPayload(contactSearchText: string): ISearchContactsPayload {
        const payload: ISearchContactsPayload = {
            contact_filter: {},
            sort_field: {
                field_name: 'first_name',
                direction: SortDirection.desc
            }
        };
        if (contactSearchText) {
            payload.contact_filter = {
                or: [
                    { first_name: { like: contactSearchText } },
                    { last_name: { like: contactSearchText } },
                    { email: { like: contactSearchText } }
                ]
            };
        }
        return payload;
    }

    getSearchEventsPayload(
        eventSearchText: string,
        participantSearchText: string,
        myOrganization: boolean,
        sortFieldName: string,
        sortDirection: SortDirection,
        eventId?: string,
    ): ISearchEventsPayload {
        const payload: ISearchEventsPayload = {
            my_organization: myOrganization,
            event_filter: {},
            participant_filter: {},
            sort_field: {
                field_name: sortFieldName,
                direction: sortDirection
            }
        };
        if (eventSearchText) {
            payload.event_filter.or = [{ name: { like: eventSearchText } }];
        }
        if (eventId) {
            payload.event_filter.and = [{ id: eventId }];
        }
        if (participantSearchText) {
            payload.participant_filter = {
                or: [
                    { first_name: { like: participantSearchText } },
                    { last_name: { like: participantSearchText } },
                    { email: { like: participantSearchText } },
                ]
            };
        }
        return payload;
    }

    sanitizePagedRequest(req: IPagedRequest): IPagedRequest {
        return { limit: req.limit, offset: req.offset };
    }

    getRegisterToActivitiesUrl(): string {
        return this.prefixWithBaseUrl(this.paths.eventActivitiesCart.register);
    }

    getEventActivitiesCartGetUrl(): string {
        return this.prefixWithBaseUrl(this.paths.eventActivitiesCart.get);
    }

    getEventActivityCalendarGetUrl(activityId: string, calendarType: CalendarType): string {
        const replacement = { activityId, calendarType };
        return this.prefixWithBaseUrl(this.replacePlaceholders(this.paths.eventActivityCalendar.get, replacement));
    }

    getEventActivitiesSearchUrl(): string {
        return this.prefixWithBaseUrl(this.paths.eventActivities.search);
    }

    getEventActivityLikeUrl(activityId: string): string {
        return this.prefixWithBaseUrl(this.replacePlaceholders(this.paths.eventActivities.like, activityId));
    }

    getEventActivityUnlikeUrl(activityId: string): string {
        return this.prefixWithBaseUrl(this.replacePlaceholders(this.paths.eventActivities.unlike, activityId));
    }

    getContactInviteUrl(): string {
        return this.prefixWithBaseUrl(this.paths.profile.inviteContact);
    }

    getContactEmailCampaignSubscriptionsGetUrl(): string {
        return this.prefixWithBaseUrl(this.paths.profile.getEmailCampaignSubscriptions);
    }

    getContactEmailCampaignSubscriptionsSetUrl(): string {
        return this.prefixWithBaseUrl(this.paths.profile.setEmailCampaignSubscriptions);
    }

    getSendPasswordResetLinkUrl(): string {
        return this.prefixWithBaseUrl(this.paths.profile.sendPasswordResetLink);
    }

    getSendLoginLinkUrl(): string {
        return this.prefixWithBaseUrl(this.paths.profile.sendLoginLink);
    }

    getSendCreateAccountLinkUrl(): string {
        return this.prefixWithBaseUrl(this.paths.profile.sendCreateAccountLink);
    }

    getAccessCodeLoginUrl(eventId: string): string {
        return this.prefixWithBaseUrl(this.replacePlaceholders(this.paths.profile.accessCodeLogIn, eventId));
    }

    getContactsSearchUrl(): string {
        return this.prefixWithContactsBaseUrl(this.paths.profile.searchContacts);
    }

    getSendConfirmationCodeUrl(): string {
        return this.prefixWithBaseUrl(this.paths.profile.sendConfirmationCode);
    }

    getProfileContactUpdateUrl(): string {
        return this.prefixWithBaseUrl(this.paths.profile.updateContact);
    }

    getProfileAuthTokenUrl(): string {
        return this.prefixWithBaseUrl(this.paths.profile.token);
    }

    getProfileOrdersGetUrl(): string {
        return this.prefixWithBaseUrl(this.paths.profile.getOrders);
    }

    getProfilePasswordChangeUrl(): string {
        return this.prefixWithBaseUrl(this.paths.profile.changePassword);
    }

    // getProfileEventsGetUrl(): string {
    //     return this.prefixWithBaseUrl(this.paths.profile.getEvents);
    // }

    getEventsSearchUrl(): string {
        return this.prefixWithBaseUrl(this.paths.event.search);
    }

    getParticipantStageActionUrl(participantID: string, stageID: string): string {
        return this.prefixWithBaseUrl(
            this.replacePlaceholders(this.paths.event.participantStageAction, { participantID, stageID})
        );
    }

    getProfilePageGetUrl(): string {
        return this.prefixWithBaseUrl(this.paths.profile.getProfilePage);
    }

    getDocumentDownloadTokenGetUrl(documentId: string): string {
        return this.prefixWithBaseUrl(this.replacePlaceholders(this.paths.document.getDownloadToken, documentId));
    }

    getDocumentUpdateUrl(documentId: string): string {
        return this.prefixWithBaseUrl(this.replacePlaceholders(this.paths.document.update, documentId));
    }

    getDocumentUploadUrl(): string {
        return this.prefixWithBaseUrl(this.paths.document.create);
    }

    getDocumentFileGetUrl(documentId: string, token: string): string {
        return this.prefixWithBaseUrl(this.replacePlaceholders(this.paths.document.getFile, {
            documentID: documentId, token: token
        }));
    }

    getPaymentProductUrl(): string {
        return this.prefixWithBaseUrl(this.paths.payment.product);
    }

    getPaymentPayUrl(): string {
        return this.prefixWithBaseUrl(this.paths.payment.pay);
    }

    getPaymentPayV2Url(): string {
        return this.prefixWithThreeDotOneBaseUrl(this.paths.payment.pay);
    }

    getPaymentThankYouPageUrl(): string {
        return this.prefixWithBaseUrl(this.paths.payment.thankYou);
    }

    getPaymentOrderGetUrl(): string {
        return this.prefixWithBaseUrl(this.paths.payment.order);
    }

    getCheckDiscountCodeUrl(): string {
        return this.prefixWithBaseUrl(this.paths.payment.checkDiscountCode);
    }

    getPaymentIntegrationWebhookUrl(integrationId: string): string {
        return this.prefixWithPaymentIntegrationBaseUrl(this.replacePlaceholders(this.paths.payment.paymentIntegrationWebhook, integrationId));
    }

    getPaymentIntegrationRedirectUrl(integrationId, transactionId: string): string {
        return this.prefixWithPaymentIntegrationBaseUrl(this.replacePlaceholders(this.paths.payment.paymentIntegrationRedirect,
            {
                integrationID: integrationId,
                transactionID: transactionId
            }
        ));
    }

    getScriptsGetAllUrl(): string {
        return this.prefixWithBaseUrl(this.paths.builder.getScripts);
    }

    getProcessFormGetUrl(): string {
        return this.prefixWithBaseUrl(this.paths.process.getForm);
    }

    getProcessFormCompleteUrl(formId: string): string {
        return this.prefixWithBaseUrl(this.replacePlaceholders(this.paths.process.completeForm, formId));
    }

    getProcessCancelUrl(): string {
        return this.prefixWithBaseUrl(this.paths.process.cancel);
    }

    getEventMenuGetUrl(): string {
        return this.prefixWithBaseUrl(this.paths.builder.getMenu);
    }

    getEventDependenciesGetUrl(): string {
        return this.prefixWithBaseUrl(this.paths.builder.getDependencies);
    }

    getHomePageGetUrl(): string {
        return this.prefixWithBaseUrl(this.paths.builder.getHomePage);
    }

    getResetPasswordPageUrl(): string {
        return this.prefixWithBaseUrl(this.paths.builder.getResetPasswordPage);
    }

    getPageGetUrl(pageId: string): string {
        return this.prefixWithBaseUrl(this.replacePlaceholders(this.paths.builder.getPage, pageId));
    }

    getEventTokenGetUrl(eventId: string): string {
        return this.prefixWithBaseUrl(this.replacePlaceholders(this.paths.event.getToken, eventId));
    }

    getEventRefreshTokenGetUrl(): string {
        return this.prefixWithBaseUrl(this.paths.event.refreshToken);
    }

    getEventPublicTokenGetUrl(eventId: string): string {
        return this.prefixWithBaseUrl(this.replacePlaceholders(this.paths.event.getPublicToken, eventId));
    }

    getEventParticipantFieldsGetUrl(): string {
        return this.prefixWithBaseUrl(this.paths.event.getParticipantFields);
    }

    getEventParticipantUpdateUrl(): string {
        return this.prefixWithBaseUrl(this.paths.event.updateParticipant);
    }

    getEventParticipantCreateUrl(): string {
        return this.prefixWithBaseUrl(this.paths.event.createParticipant);
    }

    getEventSettingsGetUrl(eventId: string): string {
        return this.prefixWithBaseUrl(this.replacePlaceholders(this.paths.event.getSettings, eventId));
    }

    getEventFieldsGetUrl(): string {
        return this.prefixWithBaseUrl(this.paths.event.getFields);
    }

    getIdentityTokenGetUrl(): string {
        return this.prefixWithBaseUrl(this.paths.identity.getToken);
    }

    getIdentityTokenFromLinkTokenGetUrl(): string {
        return this.prefixWithBaseUrl(this.paths.identity.getTokenFromLinkToken);
    }

    getIdentityImpersonateUrl(): string {
        return this.prefixWithBaseUrl(this.paths.identity.impersonate);
    }

    getSendEmailAddressPath(): string {
        return this.prefixWithBaseUrl(this.paths.identity.sendEmailAddress);
    }

    getLoginPageGetPath(eventId: string): string {
        return this.prefixWithBaseUrl(this.replacePlaceholders(this.paths.builder.getLoginPage, eventId));
    }

    /**
     * Replaces placeholders found in the replacement with the URL encoded values.
     * Hash values should not encode to the same or other placeholder
     * @param path path containing placeholders in format {fieldNameInHashObject}
     * @param replacement number / string or hash object containing fields with names refrenced in the path.
     * If the type is string or number, the path must contain single placeholder only
     * @returns path with replaced placeholders
     * @example With string: replacePlaceholders('user/{userID}/teams', '1234') -> 'user/1234/teams'
     * @example With number: replacePlaceholders('user/{userID}/teams', 1234) -> 'user/1234/teams'
     * @example With hash object:
     * replacePlaceholders('event/{eventID}/dependency{dependencyID}', {eventID: '1234', dependencyID: '5678'})
     * -> 'event/1234/dependency/5678'
     */
    replacePlaceholders(path: string, replacement: { [key: string]: string | number } | string | number): string {
        if (typeof replacement === 'string' || typeof replacement === 'number') {
            const leftBracePos = path.indexOf('{');
            const rightBracePos = path.indexOf('}', leftBracePos);
            const placeholder = path.substring(leftBracePos, rightBracePos + 1);
            return path.replace(placeholder, '' + encodeURIComponent(replacement));
        }

        const hashKeys = Object.keys(replacement);
        let result = path;
        for (const key of hashKeys) {
            result = result.replace('{' + key + '}', '' + encodeURIComponent(replacement[key]));
        }
        return result;
    }

    prefixWithPaymentIntegrationBaseUrl(path: string): string {
        return this.config.paymentIntegrationWebhookBaseUrl + path;
    }

    prefixWithBaseUrl(path: string): string {
        return this.config.baseUrl + path;
    }

    prefixWithThreeDotOneBaseUrl(path: string): string {
        return this.config.threeDotOneBaseUrl + path;
    }

    prefixWithContactsBaseUrl(path: string): string {
        return this.config.contactBaseUrl + path;
    }

    getUrlEndodedAuthObject(username: string, password: string): string {
        return this.formUrlEncode({
            username: username, password: password, grant_type: 'password', client_id: this.config.client_id
        });
    }

    /**
     * Encodes an object into x-www-form-urlencoded string
     * @param obj Object to encode
     * @returns String representing x-www-form-urlencoded object. Both object keys and values are encoded
     * @example formUrlEncode({username: 'usr123', password:'pass with spaces'}) -> 'username=use123&password=pass%20with%20spaces'
     */
    formUrlEncode(obj: any): string {
        let result = '';
        const items: string[] = [];
        Object.keys(obj).forEach(key => {
            items.push(encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]));
        });
        result = items.join('&');
        return result;
    }

    getAuthorizationHeaderFromAccessTokenResponse(response: IGetTokenResponse): string {
        return response.token_type + ' ' + response.access_token;
    }

    getAuthorizationHeaderFromRefreshTokenResponse(response: IGetTokenResponse): string {
        return response.token_type + ' ' + response.refresh_token;
    }

    getTokenInfo<TPayload>(token: string): ITokenInfo<TPayload> {
        const result = {} as ITokenInfo<TPayload>;
        result.value = token;
        const firstDotIndex = token.indexOf('.');
        const firstPart = token.substring(0, firstDotIndex);
        result.header = JSON.parse(this.base64Decode(firstPart));
        const secondDotIndex = token.indexOf('.', firstDotIndex + 1);
        const secondPart = token.substring(firstDotIndex + 1, secondDotIndex);
        result.payload = JSON.parse(this.base64Decode(secondPart));
        return result;
    }

    base64Decode(value: string): string {
        return window.atob(value);
    }

    getNow(): number {
        return Date.now();
    }

    createError(err: any): ApiError {
        const communicationError = {} as ICommunicationError;
        if (err instanceof ErrorEvent) {
            // Client side or network error
            const errEvent = err as ErrorEvent;
            communicationError.message = errEvent.message;
        } else {
            // Server responded with error
            communicationError.message = err.message;
            communicationError.body = err.error;
            communicationError.status = err.status;
            communicationError.statusText = err.statusText;
        }
        const apiError = new ApiError();
        apiError.communicationError = communicationError;
        apiError.thrownError = err;
        return apiError;
    }

    createRequest(
        method: RequestMethod,
        // config: IApiServiceConfig,
        url: string,
        authorizaionHeaderValue: string,
        body?: any,
        queryParams?: { [name: string]: string | string[] },
        headers?: { [name: string]: string | string[] },
        responseType?: 'arraybuffer' | 'blob' | 'json' | 'text'
    ): HttpRequest<any> {
        const params = new HttpParams({ fromObject: queryParams });
        const finalHeaders = this.getHeaders(headers, authorizaionHeaderValue);
        const req = new HttpRequest(method, url, body, { headers: finalHeaders, params: params, responseType: responseType });
        return req;
    }

    getHeaders(headers: { [name: string]: string | string[] }, authorizaionHeaderValue: string): HttpHeaders {
        const resultHeaders = { ...headers };
        if (authorizaionHeaderValue && !resultHeaders.Authorization) {
            resultHeaders.Authorization = authorizaionHeaderValue;
        }
        const result = new HttpHeaders(resultHeaders);
        return result;
    }

    setConfig(config: IApiServiceConfig): void {
        this.config = config;
    }

    addForwardSlash(value: string): string {
        if (value[value.length - 1] !== '/') {
            return value + '/';
        }
        return value;
    }
}
